import { FINA_PAGO_MOVIL_PHONE, FINA_PAGO_MOVIL_PHONE_DIGITS, FINA_PAYMENT_DATA_PAGO_MOVIL, FINA_PAYMENT_DATA_TRANSFERENCIA, FinaPaymentRow } from '../constants/fina-payment-info';
import { BanescoTestData } from '../models';

/** Cómo pagó el comercio (ADM-678): define qué datos se le piden y cómo lo busca el banco. */
export type CreditPaymentMethod = 'pago_movil' | 'transferencia';

/**
 * Datos de Fina a los que el comercio paga, según el método (ADM-678): un pago móvil se
 * envía a un teléfono y una transferencia a un número de cuenta. En staging se reemplazan
 * por la cuenta de QA que el API consulta (`testData.enabled`): mostrar la real haría que
 * un "no encontrado" pareciera un problema del pago. El teléfono destino no tiene
 * equivalente de calidad y se muestra el de producción.
 *
 * Fuente única para `pay-modal` y `abono-modal` (antes vivía dentro del `pay-modal`).
 */
export function finaPaymentRowsFor(method: CreditPaymentMethod, testData?: BanescoTestData | null): ReadonlyArray<FinaPaymentRow> {
    const isPagoMovil = method === 'pago_movil';
    const acc = testData?.enabled ? testData.account : null;
    if (!acc) return isPagoMovil ? FINA_PAYMENT_DATA_PAGO_MOVIL : FINA_PAYMENT_DATA_TRANSFERENCIA;

    const banco: FinaPaymentRow = { key: 'banco', label: 'BANCO', value: `${acc.bankName} (${acc.bankCode})` };
    const rif: FinaPaymentRow = { key: 'rif', label: 'RIF', value: acc.rif };
    if (isPagoMovil) {
        return [banco, { key: 'telefono', label: 'TELÉFONO', value: FINA_PAGO_MOVIL_PHONE, copyValue: FINA_PAGO_MOVIL_PHONE_DIGITS }, rif];
    }
    return [banco, { key: 'cuenta', label: 'NÚMERO DE CUENTA', value: acc.accountNumber }, rif];
}

/**
 * Formato de la referencia según método y banco de origen (espejo de `validateLocally` del
 * API): una transferencia Banesco→Banesco se busca por la referencia COMPLETA (10–12
 * dígitos, ADM-810); el resto, por los últimos 6.
 */
export function requiresFullReference(method: CreditPaymentMethod, bankCode: string | null | undefined): boolean {
    return method === 'transferencia' && bankCode === '0134';
}

/**
 * Largo de la referencia completa Banesco→Banesco (ADM-810). Espejo de
 * `BANESCO_TRANSFER_REFERENCE_MIN_DIGITS` / `_MAX_DIGITS` de `api`: el rango vive solo aquí y de él salen
 * el patrón, la regex y el texto del campo de pagar cuota, abonar y desbloquear.
 */
export const FULL_REFERENCE_MIN_DIGITS = 10;
export const FULL_REFERENCE_MAX_DIGITS = 12;
/** Para el atributo `pattern` de `credit-blocked-modal` (sin anclas). */
export const FULL_REFERENCE_PATTERN = `\\d{${FULL_REFERENCE_MIN_DIGITS},${FULL_REFERENCE_MAX_DIGITS}}`;
export const FULL_REFERENCE_RE = new RegExp(`^${FULL_REFERENCE_PATTERN}$`);

/**
 * Cuántos dígitos pide el campo de referencia (FINA-3378): va SIEMPRE bajo el campo, no solo como error,
 * y es el mismo texto en pagar cuota, abonar y desbloquear. Con un valor inválido ya tocado, se pinta como error.
 */
export function referenceDigitsMessage(full: boolean): string {
    return full ? `Escribe la referencia completa: de ${FULL_REFERENCE_MIN_DIGITS} a ${FULL_REFERENCE_MAX_DIGITS} dígitos.` : 'Escribe los últimos 6 dígitos de la referencia.';
}
